import { ChangeDetectionStrategy, Component, computed, effect, Inject, inject, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { SystemStateWidgetStore } from './stores/system-state-widget.store';
import { SystemState } from './models/system-state.model';
import { AppTableLoadingOverlayComponent } from "src/app/shared/components/loading-overlay/table-loading-overlay.component";



type Severity = "success" | "danger" | "secondary" | "info" | "warn" | "contrast" | undefined;

@Component({
  selector: 'app-system-state-widget',
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, TagModule, SkeletonModule, AppTableLoadingOverlayComponent],
  templateUrl: './system-state-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemStateWidgetComponent {
  widgetHeader: WritableSignal<string> = signal('');
  
  private readonly store = inject(SystemStateWidgetStore);

  constructor(@Inject('label') public label: any) {
    this.widgetHeader.set(this.label);

    effect(() => {
      // beim ersten Render einmal laden
      this.refresh();
    });
  }

  readonly vm = computed(() => {
    const current = this.store.systemState();

    const label = this.stateLabel(current.state);
    const severity = this.stateSeverity(current.state);
    const dotClass = this.stateDotClass(current.state);

    return {
      state: current.state,
      label,
      severity,
      dotClass,
      message: current.message ?? '',
    };
  });

  readonly lastUpdated = signal<Date | null>(null);

  refresh(): void {
    this.store.loadSystemState();
    this.lastUpdated.set(new Date());
  }

  // --- UI mapping helpers ---

  private stateLabel(state: SystemState): string {
    switch (state) {
      case SystemState.ONLINE:
        return 'Online';
      case SystemState.OFFLINE:
        return 'Offline';
      case SystemState.MAINTENANCE:
        return 'Wartung';
      case SystemState.PARTIAL_ONLINE:
        return 'Teilweise online';
      case SystemState.OFFLINE_OR_MAINTENANCE:
        return 'Alle Systeme offline oder in Wartung';
      default:
        return 'Unbekannt';
    }
  }

  private stateSeverity(state: SystemState): Severity {
    switch (state) {
      case SystemState.ONLINE:
        return 'success';
      case SystemState.OFFLINE:
      case SystemState.OFFLINE_OR_MAINTENANCE:
        return 'danger';
      case SystemState.MAINTENANCE:
      case SystemState.PARTIAL_ONLINE:
        case SystemState.UNKNOWN:
        return 'warn';

      default:
        return 'secondary';
    }
  }

  private stateDotClass(state: SystemState): string {
    // Tailwind classes für den Punkt
    switch (state) {
      case SystemState.ONLINE:
        return 'bg-emerald-500';
      case SystemState.OFFLINE:
        case SystemState.OFFLINE_OR_MAINTENANCE:
        return 'bg-red-500';
      case SystemState.MAINTENANCE:
        case SystemState.PARTIAL_ONLINE:
        return 'bg-amber-500';
      default:
        return 'bg-slate-400';
    }
  }
}
